import { QueryClientProvider } from '@tanstack/react-query'
import { createRouter, RouterProvider } from '@tanstack/react-router'
import { Toaster } from 'sonner'
import { useShallow } from 'zustand/shallow'

import { routeTree } from '@/routeTree.gen'
import { queryClient } from '@/shared/api/query-client'
import { useAuthStore } from '@/shared/auth/auth-store'
import { RouteError } from '@/widgets/app-shell/route-error'
import { RoutePending } from '@/widgets/app-shell/route-pending'

export const router = createRouter({
  routeTree,
  // auth é sobrescrito pelo InnerApp a cada render; queryClient é estável.
  context: { auth: { status: 'loading', roles: [] }, queryClient },

  /**
   * O build faz code splitting por rota (~155 chunks). SEM preload, um clique no
   * menu dispara três idas à rede EM SÉRIE antes de qualquer pixel mudar:
   * chunk da rota → chunk do namespace i18n → loader (ensureQueryData).
   *
   * 'intent' antecipa as três no hover/foco do link, então o clique cai no que
   * já está quente. `defaultPreloadDelay` (50ms, default) evita disparar
   * preload em quem só passa o mouse de raspão pela sidebar.
   */
  defaultPreload: 'intent',

  /**
   * 0 = o router NÃO guarda cache próprio do loader; quem decide o que está
   * fresco é o React Query (staleTime dele).
   *
   * O default é 30s, e isso empilha DOIS caches com regras diferentes sobre o
   * mesmo dado — a tela mostra um estado e troca depois, do nada. Como todo
   * loader daqui é `ensureQueryData`, deixar o Query mandar sozinho é o
   * recomendado quando se usa cache externo.
   */
  defaultPreloadStaleTime: 0,

  /**
   * Feedback de navegação. Os dois números importam:
   *   - PendingMs 150: abaixo disso a troca é percebida como instantânea e um
   *     skeleton só piscaria.
   *   - PendingMinMs 300: uma vez visível, fica no ar o mínimo para não virar
   *     flash. Sem ele, um loader de 160ms acende e apaga o skeleton.
   */
  defaultPendingMs: 150,
  defaultPendingMinMs: 300,
  defaultPendingComponent: RoutePending,

  /** Antes não havia fronteira de erro: loader que rejeitava levava a app ao fallback cru. */
  defaultErrorComponent: RouteError,
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

/**
 * O contexto do router precisa ser REATIVO ao auth: se fosse lido uma vez, a
 * guarda continuaria vendo o status antigo depois do login e o usuário ficaria
 * preso no redirect.
 */
function InnerApp() {
  // useShallow porque o selector devolve OBJETO: sem ele, referência nova a cada
  // render → Object.is falha → loop infinito. O React Compiler não salva disso
  // (memoiza o render, não o snapshot de uma store externa).
  const auth = useAuthStore(useShallow((s) => ({ status: s.status, roles: s.roles })))
  return <RouterProvider router={router} context={{ auth, queryClient }} />
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <InnerApp />
      {/* Sem isto, todo `toast.*` do app é engolido em silêncio — o código roda,
          nada aparece. Fácil de esquecer e difícil de notar (não dá erro). */}
      <Toaster richColors closeButton position="bottom-right" />
    </QueryClientProvider>
  )
}
